import { useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Order status matching producer portal workflow
export type OrderStatus = 'new' | 'in_progress' | 'ready' | 'completed' | 'cancelled';

// Financial status
export type FinancialStatus = 'paid' | 'pending' | 'cancelled';

// Fulfillment status
export type FulfillmentStatus = 'unfulfilled' | 'ready' | 'fulfilled' | null;

// Valid status values
const VALID_STATUSES: OrderStatus[] = ['new', 'in_progress', 'ready', 'completed', 'cancelled'];

// Helper to ensure valid status
function ensureValidStatus(status: string | null | undefined): OrderStatus {
  if (status && VALID_STATUSES.includes(status as OrderStatus)) {
    return status as OrderStatus;
  }
  return 'new';
}

// Map portal status back to orders table status.
// 'new' maps to 'created' (not 'paid') — only Stripe webhook should set 'paid'.
function portalStatusToOrderStatus(portalStatus: OrderStatus): string {
  switch (portalStatus) {
    case 'new':
      return 'created';
    case 'in_progress':
      return 'processing';
    default:
      return portalStatus;
  }
}

export interface DeliveryAddress {
  street?: string;
  postal_code?: string;
  city?: string;
}

export type PaymentStatus = 'unpaid' | 'paid';

export interface ProducerOrder {
  id: string;
  producer_id: string;
  order_number: string;
  status: OrderStatus;
  financial_status: FinancialStatus;
  fulfillment_status: FulfillmentStatus;
  payment_status: PaymentStatus;
  paid_at: string | null;
  paid_method: string | null;
  total_amount: number;
  currency: string;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  delivery_type: 'pickup' | 'delivery';
  shipping_option_id: string | null;
  delivery_address: DeliveryAddress | null;
  shipping_fee_amount: number;
  subtotal_amount: number;
  channel: string;
  source_checkout_id: string | null;
  created_at: string;
  updated_at: string;
  user_id: string | null;
}

export interface OrderLineItem {
  id: string;
  product_id: string | null;
  title: string;
  quantity: number;
  unit_amount: number;
  total_amount: number;
  sku: string | null;
  metadata: Record<string, unknown>;
}

export interface OrderFilters {
  status?: string;
  dateRange?: 'all' | '7d' | '30d';
  search?: string;
}

const ORDER_QUERY_STALE_TIME_MS = 60_000;
const ORDER_COUNT_REFETCH_MS = 120_000;

export function useProducerOrders(producerId: string | undefined, filters: OrderFilters = {}) {
  return useQuery({
    queryKey: ['producer-orders', producerId, filters],
    queryFn: async () => {
      if (!producerId) return [];

      // Query the view which handles status mapping
      let query = supabase
        .from('producer_portal_orders_v' as any)
        .select('*')
        .eq('producer_id', producerId)
        .order('created_at', { ascending: false });

      // Filter by status
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      // Filter by date range
      if (filters.dateRange && filters.dateRange !== 'all') {
        const days = filters.dateRange === '7d' ? 7 : 30;
        const fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - days);
        query = query.gte('created_at', fromDate.toISOString());
      }

      // Search by order number, customer email, or customer name
      if (filters.search) {
        query = query.or(`order_number.ilike.%${filters.search}%,customer_email.ilike.%${filters.search}%,customer_name.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Map to ProducerOrder type with status validation
      return ((data || []) as any[]).map((row): ProducerOrder => ({
        ...row,
        status: ensureValidStatus(row.status),
        financial_status: row.financial_status || 'pending',
        fulfillment_status: row.fulfillment_status || null,
        payment_status: row.payment_status || 'unpaid',
        paid_at: row.paid_at || null,
        paid_method: row.paid_method || null,
        shipping_option_id: row.shipping_option_id || null,
        delivery_address: row.delivery_address || null,
      }));
    },
    enabled: !!producerId,
    staleTime: ORDER_QUERY_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });
}

export function useNewOrderCount(producerId: string | undefined) {
  return useQuery({
    queryKey: ['producer-orders-new-count', producerId],
    queryFn: async () => {
      if (!producerId) return 0;

      // Count orders with 'created' or 'paid' status (both map to portal 'new').
      // 'created' = Stripe paused; 'paid' = Stripe active.
      const { count, error } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('producer_id', producerId)
        .in('status', ['created', 'paid']);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!producerId,
    staleTime: ORDER_QUERY_STALE_TIME_MS,
    refetchInterval: ORDER_COUNT_REFETCH_MS, // Backup only; realtime invalidation remains primary
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });
}

export function useProducerOrder(orderId: string | undefined) {
  return useQuery({
    queryKey: ['producer-order', orderId],
    queryFn: async () => {
      if (!orderId) return null;

      // Use orders table directly to get all fields including delivery_address
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('id', orderId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;
      
      // Map orders table status to portal status.
      // Both 'created' (Stripe paused) and 'paid' (Stripe active) => portal 'new'.
      const mapStatus = (status: string): OrderStatus => {
        if (status === 'created' || status === 'paid') return 'new';
        if (status === 'processing') return 'in_progress';
        if (VALID_STATUSES.includes(status as OrderStatus)) return status as OrderStatus;
        return 'new';
      };
      
      return {
        id: data.id,
        producer_id: data.producer_id,
        order_number: data.external_order_id || data.id.slice(0, 8),
        status: mapStatus(data.status),
        financial_status: data.status === 'paid' || data.status === 'processing' || data.status === 'ready' || data.status === 'completed' ? 'paid' : 'pending',
        fulfillment_status: null,
        payment_status: (data as any).payment_status || 'unpaid',
        paid_at: (data as any).paid_at || null,
        paid_method: (data as any).paid_method || null,
        total_amount: data.total_amount,
        currency: data.currency,
        customer_name: data.customer_name,
        customer_email: data.customer_email,
        customer_phone: data.customer_phone,
        delivery_type: data.delivery_type || 'pickup',
        shipping_option_id: data.shipping_option_id,
        delivery_address: data.delivery_address as DeliveryAddress | null,
        shipping_fee_amount: data.shipping_fee_amount,
        subtotal_amount: data.subtotal_amount,
        channel: data.channel,
        source_checkout_id: data.source_checkout_id,
        created_at: data.created_at,
        updated_at: data.updated_at,
        user_id: data.user_id,
      } as ProducerOrder;
    },
    enabled: !!orderId,
    staleTime: ORDER_QUERY_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });
}

// Fetch order line items from order_items table
export function useOrderItems(orderId: string | undefined) {
  return useQuery({
    queryKey: ['order-items', orderId],
    queryFn: async () => {
      if (!orderId) return [];

      const { data, error } = await supabase
        .from('order_items')
        .select('id, product_id, title, quantity, unit_amount, total_amount, sku, metadata')
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data || []) as OrderLineItem[];
    },
    enabled: !!orderId,
    staleTime: ORDER_QUERY_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      orderId, 
      status, 
    }: { 
      orderId: string; 
      status: OrderStatus; 
    }) => {
      // Convert portal status to orders table status
      const orderStatus = portalStatusToOrderStatus(status);
      
      const { data, error } = await supabase
        .from('orders')
        .update({ status: orderStatus })
        .eq('id', orderId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['producer-orders'] });
      queryClient.invalidateQueries({ queryKey: ['producer-orders-new-count'] });
      queryClient.invalidateQueries({ queryKey: ['producer-order', data.id] });
      queryClient.invalidateQueries({ queryKey: ['farmly-orders'] });
      queryClient.invalidateQueries({ queryKey: ['farmly-orders-new-count'] });
    },
  });
}

// Realtime notifier hook for new orders
export function useNewOrderNotifier(
  producerId: string | undefined,
  onNewOrder?: (order: ProducerOrder) => void
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (!producerId) return;

    const channel = supabase
      .channel(`orders-${producerId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'orders',
          filter: `producer_id=eq.${producerId}`,
        },
        (payload) => {
          const newOrder = payload.new as any;
          
          // Map to ProducerOrder format
          // Map realtime order to portal format.
          // 'created' (Stripe paused) and 'paid' (Stripe active) both => portal 'new'.
          const order: ProducerOrder = {
            ...newOrder,
            order_number: newOrder.external_order_id || newOrder.id.slice(0, 8),
            status: (newOrder.status === 'created' || newOrder.status === 'paid') ? 'new'
              : newOrder.status === 'processing' ? 'in_progress'
              : ensureValidStatus(newOrder.status),
            financial_status: newOrder.status === 'paid' ? 'paid' : 'pending',
            fulfillment_status: null,
            total_amount: newOrder.total_amount,
          };
          
          // Invalidate queries to update counts and lists
          queryClient.invalidateQueries({ queryKey: ['producer-orders'] });
          queryClient.invalidateQueries({ queryKey: ['producer-orders-new-count'] });

          // Show toast notification
          toast({
            title: '📦 Ny order inkom!',
            description: `Order #${order.order_number}`,
          });

          // Call callback if provided
          onNewOrder?.(order);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [producerId, queryClient, toast, onNewOrder]);
}

// Helper to count items in order
export function countOrderItems(items: OrderLineItem[] | undefined): number {
  if (!items || items.length === 0) return 0;
  return items.reduce((sum, item) => sum + item.quantity, 0);
}

// Helper to format item count label in Swedish
export function formatItemCountLabel(items: OrderLineItem[] | undefined): string {
  if (!items || items.length === 0) return '0 artiklar';
  const lineCount = items.length;
  if (lineCount === 1) {
    const item = items[0];
    return item.quantity > 1 ? `${item.title} ×${item.quantity}` : item.title;
  }
  return `${lineCount} artiklar`;
}

// Helper to format price
export function formatOrderPrice(amount: number | null, currency: string): string {
  if (amount === null) return '—';
  // amount is in öre (cents), convert to kronor
  const value = amount / 100;
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: currency || 'SEK',
  }).format(value);
}

// Helper to format price without decimals (e.g. "250 kr")
export function formatOrderPriceWhole(amount: number | null, currency: string): string {
  if (amount === null) return '—';
  const value = amount / 100;
  return `${Math.round(value).toLocaleString('sv-SE')} kr`;
}

// Status labels in Swedish
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  new: 'Ny',
  in_progress: 'Pågår',
  ready: 'Redo för upphämtning',
  completed: 'Utlämnad',
  cancelled: 'Avbruten',
};

/**
 * Mutation to mark an order as manually paid (Swish, cash, invoice, etc.).
 * Updates payment_status, paid_at and paid_method on the orders table.
 * Does NOT touch orders.status (logistics flow is separate).
 */
export function useMarkOrderPaid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      method,
      undo = false,
    }: {
      orderId: string;
      method: string;
      undo?: boolean;
    }) => {
      const { data, error } = await supabase
        .from('orders')
        .update(
          undo
            ? { payment_status: 'unpaid', paid_at: null, paid_method: null } as any
            : { payment_status: 'paid', paid_at: new Date().toISOString(), paid_method: method } as any
        )
        .eq('id', orderId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['producer-orders'] });
      queryClient.invalidateQueries({ queryKey: ['producer-order', data.id] });
      queryClient.invalidateQueries({ queryKey: ['farmly-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pack-list'] });
    },
  });
}
